import React, { Suspense, lazy } from "react";
import { BrowserRouter as Router, Route } from "react-router-dom";

import { Container } from "react-bootstrap";
import Footer from "./components/Footer";
import Header from "./components/Header/Header";
import Loader from "./components/Loader";
import AuthVerify from "./common/auth-verify";

// The routes below are split in two.
//
// Eagerly imported: the path a visitor who has never signed in actually
// walks - the home page, an event and its registration, the payment step,
// donation, sign-in and account activation, the board and document pages,
// about. These are in the first download, so nobody standing in front of a
// registration form waits on a second request to see it.
//
// Lazily imported: everything else, which is to say every administrative
// screen and every page reached by a deliberate second navigation. Each
// becomes its own chunk, fetched the first time its route matches. An admin
// pays that cost once, after signing in; an anonymous visitor never pays it
// at all.

import HomeScreen from "./screens/HomeScreen/HomeScreen";
import LoginScreen from "./screens/LoginScreen/LoginScreen";
import RegisterScreen from "./screens/RegistrationScreen/RegisterScreen";
import PasswordResetScreen from "./screens/PasswordResetScreen/PasswordResetScreen";
import PasswordUpdateScreen from "./screens/PasswordUpdateScreen/PasswordUpdateScreen";
import EmailActivationScreen from "./screens/EmailActivationScreen/EmailActivationScreen";
import About from "./screens/AboutScreen/About";
import BodScreen from "./screens/CommitteesScreen/BodScreen";
import UploadScreen from "./screens/UploadScreen/UploadScreen";
import Document from "./screens/UploadScreen/Document";
import DonateScreen from "./screens/DonateScreen/DonateScreen";
import EventByIdScreen from "./screens/EventScreen/EventByIdScreen";
import EventRegisterScreen from "./screens/EventScreen/EventRegistrationScreen";
import EventRegistrationPayment from "./screens/EventScreen/EventRegistrationPayment";

const DashboardScreen = lazy(() =>
  import("./screens/DashboardScreen/DashboardScreen")
);
const UserListScreen = lazy(() => import("./screens/UserListScreen"));
const UserEditScreen = lazy(() => import("./screens/UserEditScreen"));
const UserPendingApproveScreen = lazy(() =>
  import("./screens/UserPendingApproveScreen/UserPendingApproveScreen")
);
const ProfileScreen = lazy(() =>
  import("./screens/ProfileScreen/ProfileScreen")
);
const EditProfileScreen = lazy(() =>
  import("./screens/ProfileScreen/EditProfileScreen")
);
const SystemAdminScreen = lazy(() =>
  import("./screens/SystemAdminScreen/SystemAdminScreen")
);
const SettingScreen = lazy(() =>
  import("./screens/SettingScreen/SettingScreen")
);
const ChapterScreen = lazy(() =>
  import("./screens/ChapterScreen/ChapterScreen")
);
const MembersScreen = lazy(() =>
  import("./screens/MembersScreen/MembersScreen")
);
const MemberTypeScreen = lazy(() =>
  import("./screens/MemberTypeScreen/MemberTypeScreen")
);
const PaymentScreen = lazy(() =>
  import("./screens/PaymentScreen/PaymentScreen")
);
const PaymentTypeScreen = lazy(() =>
  import("./screens/PaymentTypeScreen/PaymentTypeScreen")
);
const DonationTypeScreen = lazy(() =>
  import("./screens/DonationTypeScreen/DonationTypeScreen")
);
const AdvertisementScreen = lazy(() =>
  import("./screens/AdvertisementScreen/AdvertisementScreen")
);
const EmailScreen = lazy(() => import("./screens/EmailScreen/EmailScreen"));
const EmailViewScreen = lazy(() =>
  import("./screens/EmailScreen/EmailViewScreen")
);
const AnnouncementScreen = lazy(() =>
  import("./screens/AnnouncementScreen/AnnouncementScreen")
);
const Announcement = lazy(() =>
  import("./screens/AnnouncementScreen/Announcement")
);
const MissionScreen = lazy(() =>
  import("./screens/MissionScreen/MissionScreen")
);
const Mission = lazy(() => import("./screens/MissionScreen/Mission"));
const VissionScreen = lazy(() =>
  import("./screens/VissionScreen/VissionScreen")
);
const Vission = lazy(() => import("./screens/VissionScreen/Vission"));
const HistoryScreen = lazy(() =>
  import("./screens/HistoryScreen/HistoryScreen")
);
const History = lazy(() => import("./screens/HistoryScreen/History"));
const CommittieesScreen = lazy(() =>
  import("./screens/CommitteesScreen/CommitteesScreen")
);
const CommitteeMemberScreen = lazy(() =>
  import("./screens/CommitteesScreen/CommitteeMemberScreens")
);
const TrainingScreen = lazy(() =>
  import("./screens/TrainingScreen/TrainingScreen")
);
const ImagesScreen = lazy(() => import("./screens/ImagesScreen/ImagesScreen"));
// const ImageByEventScreen = lazy(() => import('./screens/ImageByEventScreen/ImageByEventScreen'));
const ImageByIdScreen = lazy(() =>
  import("./screens/ImagesScreen/ImageByIdScreen")
);
const BlogScreen = lazy(() => import("./screens/BlogScreen/BlogScreen"));
const CreateBlogScreen = lazy(() =>
  import("./screens/BlogScreen/CreateBlogScreen")
);
const BlogScreenById = lazy(() =>
  import("./screens/BlogScreen/BlogScreenById")
);
const JobScreen = lazy(() => import("./screens/CareerScreen/JobScreen"));
const JobAllScreen = lazy(() => import("./screens/CareerScreen/JobAllScreen"));
const JobByIdScreen = lazy(() =>
  import("./screens/CareerScreen/JobByIdScreen")
);
const CreateJobScreen = lazy(() =>
  import("./screens/CareerScreen/CreateJobScreen")
);
const JobPostEditScreen = lazy(() =>
  import("./screens/CareerScreen/JobPostEditScreen")
);
const CareerScreen = lazy(() => import("./screens/CareerScreen/CareerScreen"));
const NewsLetterUnSubScreen = lazy(() =>
  import("./screens/CareerScreen/NewsLetterUnSubScreen")
);
const ActivityScreen = lazy(() =>
  import("./screens/ActivityScreen/ActivityScreen")
);
const ActivityByIdScreen = lazy(() =>
  import("./screens/ActivityScreen/ActivityByIdScreen")
);
const HelpContactScreen = lazy(() =>
  import("./screens/HelpContactScreen/HelpContactScreen")
);
const QuickLinkScreen = lazy(() =>
  import("./screens/QuickLinkScreen/QuickLinkScreen")
);
const EventScreen = lazy(() => import("./screens/EventScreen/EventScreen"));
const EventNewScreen = lazy(() =>
  import("./screens/EventScreen/EventNewScreen")
);
const EventNewScreenNew = lazy(() =>
  import("./screens/EventScreen/EventNewScreenNew")
);
const EventRegistrationReportScreen = lazy(() =>
  import("./screens/EventScreen/EventRegistrationReportScreen")
);
const EventRegPayNew = lazy(() =>
  import("./screens/EventScreen/EventRegPayNew")
);
const CentralEventModReg = lazy(() =>
  import(
    "./components/CentralEvent/BiAnnualEvent2022/ModRegister/CentralEventModReg"
  )
);

const App = () => {
  return (
    <>
      <Router>
        <Header />
        <main className="py-3">
          {/* One boundary for the whole route area: an eager route renders
              straight through it, a lazy one shows the site's spinner for as
              long as its chunk takes to arrive. */}
          <Suspense fallback={<Loader />}>
            <Container>
              <Route path="/about" component={About} />
              <Route path="/login" component={LoginScreen} />
              <Route path="/password/recover" component={PasswordResetScreen} />
              <Route
                path="/password/reset/:id/:token"
                component={PasswordUpdateScreen}
              />
              <Route path="/register" component={RegisterScreen} />
              <Route path="/activate/:hash" component={EmailActivationScreen} />
              <Route path="/dashboardScreen" component={UserListScreen} />
              <Route path="/dashboard" component={DashboardScreen} />
              <Route path="/blog/new" component={CreateBlogScreen} exact />
              <Route path="/blogs/:id" component={BlogScreenById} />
              <Route
                exact
                path="/blog/search/:keyword/page/:pageNumber"
                component={BlogScreen}
              />
              <Route
                exact
                path="/blog/search/:keyword"
                component={BlogScreen}
              />
              <Route
                exact
                path="/blog/page/:pageNumber"
                component={BlogScreen}
              />
              <Route exact path="/blog" component={BlogScreen} />

              <Route path="/jobs/search/:keyword" component={JobScreen} />
              <Route path="/jobs/page/:pageNumber" component={JobScreen} />
              <Route exact path="/jobs" component={JobScreen} />
              <Route path="/jobsall/search/:keyword" component={JobAllScreen} />
              <Route
                path="/jobsall/page/:pageNumber"
                component={JobAllScreen}
              />
              <Route exact path="/jobsall" component={JobAllScreen} />
              <Route path="/job/new" component={CreateJobScreen} />
              <Route exact path="/jobs/:id" component={JobByIdScreen} />
              <Route
                path="/newsletter/optout"
                component={NewsLetterUnSubScreen}
              />
              <Route path="/career/edit/:id" component={JobPostEditScreen} />
              <Route
                exact
                path="/career/:pagetitle?"
                component={CareerScreen}
              />

              <Route path="/activity" component={ActivityScreen} exact />
              <Route path="/activity/:id" component={ActivityByIdScreen} />

              <Route path="/systemAdmin" component={SystemAdminScreen} />
              <Route path="/chapter" component={ChapterScreen} />
              <Route
                path="/advertisement/:advtitle?"
                component={AdvertisementScreen}
              />

              <Route path="/mission" component={MissionScreen} />
              <Route path="/chapters/mission/:id" component={Mission} />
              <Route path="/announcement" component={AnnouncementScreen} />
              <Route path="/help" component={HelpContactScreen} />
              <Route path="/links" component={QuickLinkScreen} />
              <Route path="/announcements/:id" component={Announcement} />
              <Route path="/vission" component={VissionScreen} />
              <Route path="/chapters/vission/:id" component={Vission} />
              <Route path="/history" component={HistoryScreen} />
              <Route path="/chapters/history/:id" component={History} />
              <Route path="/memberType" component={MemberTypeScreen} />
              <Route path="/paymentType" component={PaymentTypeScreen} />
              <Route path="/donationType" component={DonationTypeScreen} />
              <Route path="/images" component={ImagesScreen} />
              {/* <Route path='/image/event' component={ImageByEventScreen} /> */}
              <Route path="/image/:id" component={ImageByIdScreen} />
              <Route path="/payment" component={PaymentScreen} />
              <Route path="/donate" component={DonateScreen} />
              <Route path="/training" component={TrainingScreen} />
              <Route path="/bod" component={BodScreen} />
              <Route path="/committiees" component={CommittieesScreen} />
              <Route path="/committee/:id" component={CommitteeMemberScreen} />
              <Route path="/documents" component={UploadScreen} />
              <Route path="/doc/:id" component={Document} />
              <Route path="/members" component={MembersScreen} />
              <Route path="/emails" component={EmailScreen} />
              <Route path="/email/:id" component={EmailViewScreen} />
              <Route path="/events" component={EventScreen} />
              <Route
                path="/event/register/:id"
                component={EventRegisterScreen}
              />
              <Route
                path="/event/report/:id"
                component={EventRegistrationReportScreen}
              />
              <Route path="/event/regmod/:id" component={CentralEventModReg} />
              <Route path="/eventsnew" component={EventNewScreen} />
              <Route
                path="/event/registration/payment"
                component={EventRegistrationPayment}
              />
              <Route path="/event/payment" component={EventRegPayNew} />
              <Route path="/eventnew" component={EventNewScreenNew} />
              <Route path="/settings" component={SettingScreen} />
              <Route
                path="/users/:id/pending"
                component={UserPendingApproveScreen}
              />
              <Route path="/profile" component={ProfileScreen} />
              <Route path="/users/profile/edit" component={EditProfileScreen} />
              <Route path="/admin/users/:id/edit" component={UserEditScreen} />
              <Route path="/" component={HomeScreen} exact />
            </Container>
            <Route path="/event/:id" component={EventByIdScreen} exact />
          </Suspense>
        </main>
        <Footer />
        <AuthVerify />
      </Router>
    </>
  );
};

export default App;
